import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toast';
import {
  LuRadar, LuLoader, LuCheck, LuX, LuFileText, LuLayers, LuClipboardList,
  LuGlobe, LuRotateCcw, LuArrowRight
} from 'react-icons/lu';
import useDiscoveryProgressSocket from '../../../hooks/useDiscoveryProgressSocket';
import { formatSecondsToMinSec } from '../../../utils/formatSecondToMinSec';

const COMPLETE_REDIRECT_DELAY_MS = 1500;

const normalizeRunStatus = (status) => {
  const value = String(status || '').toLowerCase();
  if (['completed', 'complete', 'success', 'succeeded', 'finished', 'done'].includes(value)) {
    return 'completed';
  }
  if (['failed', 'fail', 'error', 'cancelled', 'canceled'].includes(value)) {
    return 'failed';
  }
  return 'running';
};

const ACTIVITY_ITEMS = [
  'Navigating through pages and following links',
  'Identifying all interactive elements (buttons, inputs, links)',
  'Generating stable selectors for each element',
  'Building a comprehensive UI Map of your application'
];

const StatTile = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-sm">
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone.bg}`}>
      <Icon size={22} className={tone.text} />
    </div>
    <div className="min-w-0">
      <div className={`text-2xl font-bold leading-tight tabular-nums ${tone.text}`}>{value}</div>
      <div className="truncate text-xs font-medium text-[color:var(--text-muted)]">{label}</div>
    </div>
  </div>
);

/**
 * Live crawl progress for the in-app "Crawl Project" page. Uses the same
 * `discovery_progress` websocket frames as the wizard's Step7CDiscoveryCrawl.
 */
const CrawlProgressPanel = ({ runId, baseUrl, onCompleted, onRetry }) => {
  const completionHandledRef = useRef(false);
  const redirectTimerRef = useRef(null);
  const [progress, setProgress] = useState({
    status: 'running',
    progress: 0,
    pagesDiscovered: 0,
    elementsMapped: 0,
    formsMapped: 0,
    currentPage: '',
    timeTakenSeconds: 0,
    estimatedTimeRemainingSeconds: null
  });

  const { progress: wsProgress, status: socketStatus } = useDiscoveryProgressSocket({
    runId,
    enabled: Boolean(runId)
  });

  useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

  useEffect(() => {
    if (!wsProgress) return;

    const status = normalizeRunStatus(wsProgress.run_status);
    setProgress({
      status,
      progress: Math.min(100, Math.round(Number(wsProgress.progress_percentage) || 0)),
      pagesDiscovered: Number(wsProgress.pages_discovered) || 0,
      elementsMapped: Number(wsProgress.elements_mapped) || 0,
      formsMapped: Number(wsProgress.forms_mapped) || 0,
      currentPage: wsProgress.current_url || '',
      timeTakenSeconds: Number(wsProgress.time_taken_seconds) || 0,
      estimatedTimeRemainingSeconds:
        wsProgress.estimated_time_remaining_seconds == null
          ? null
          : Number(wsProgress.estimated_time_remaining_seconds)
    });

    if (status === 'completed' && !completionHandledRef.current) {
      completionHandledRef.current = true;
      toast.success('UI discovery completed!');
      redirectTimerRef.current = setTimeout(onCompleted, COMPLETE_REDIRECT_DELAY_MS);
    }

    if (status === 'failed' && !completionHandledRef.current) {
      completionHandledRef.current = true;
      toast.error('UI discovery failed');
    }
  }, [wsProgress, onCompleted]);

  const isCompleted = progress.status === 'completed';
  const isFailed = progress.status === 'failed';
  const isRunning = !isCompleted && !isFailed;
  const hasStarted = Boolean(wsProgress);

  const heroTone = isCompleted
    ? { ring: 'border-[color-mix(in_srgb,var(--success)_35%,transparent)] bg-[var(--success-muted)]', icon: 'bg-emerald-500', title: 'text-[color:var(--success)]' }
    : isFailed
      ? { ring: 'border-[color-mix(in_srgb,var(--danger)_35%,transparent)] bg-[var(--danger-muted)]', icon: 'bg-red-500', title: 'text-[color:var(--danger)]' }
      : { ring: 'border-[color-mix(in_srgb,var(--primary)_35%,transparent)] bg-[var(--primary-muted)]', icon: 'bg-[var(--primary-color)]', title: 'text-[var(--primary-color)]' };

  const heroSubtitle = isCompleted
    ? `Discovered ${progress.pagesDiscovered} pages and ${progress.elementsMapped} elements`
    : isFailed
      ? 'An error occurred during discovery'
      : hasStarted
        ? `${progress.progress}% complete`
        : 'Waiting for the crawler to report progress...';

  return (
    <div className="space-y-5">
      {/* Status hero */}
      <div className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${heroTone.ring}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-md ${heroTone.icon}`}>
            {isCompleted ? <LuCheck size={28} /> : isFailed ? <LuX size={28} /> : <LuLoader size={28} className="animate-spin" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={`text-lg font-semibold sm:text-xl ${heroTone.title}`}>
                {isCompleted ? 'Discovery Complete!' : isFailed ? 'Discovery Failed' : 'Crawling Application...'}
              </h2>
              {isRunning && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--text-secondary)] ring-1 ring-[var(--border)]">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      socketStatus === 'connected' ? 'animate-pulse bg-emerald-500' : 'bg-amber-400'
                    }`}
                  />
                  {socketStatus === 'connected' ? 'Live' : 'Connecting'}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-sm text-[color:var(--text-secondary)]">{heroSubtitle}</p>
            {baseUrl && (
              <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-[color:var(--text-muted)]">
                <LuGlobe size={13} className="shrink-0" />
                <span className="truncate">{baseUrl}</span>
              </p>
            )}
          </div>
          {isCompleted && (
            <button
              type="button"
              onClick={onCompleted}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 sm:w-auto"
            >
              View results <LuArrowRight size={16} />
            </button>
          )}
        </div>

        {/* Progress bar */}
        {!isFailed && (
          <div className="mt-5">
            <div className="h-2.5 overflow-hidden rounded-full bg-[var(--surface)] ring-1 ring-inset ring-[var(--border)]">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ease-out ${
                  isCompleted ? 'bg-emerald-500' : 'bg-[var(--primary-color)]'
                }`}
                style={{ width: `${isCompleted ? 100 : progress.progress}%` }}
              />
            </div>
            <div className="mt-2 flex flex-col gap-1 text-xs text-[color:var(--text-muted)] sm:flex-row sm:items-center sm:justify-between">
              <span className="font-semibold tabular-nums text-[color:var(--text-secondary)]">{isCompleted ? 100 : progress.progress}%</span>
              {isRunning && (
                <span className="tabular-nums">
                  {progress.estimatedTimeRemainingSeconds == null
                    ? `Elapsed: ${formatSecondsToMinSec(progress.timeTakenSeconds)}`
                    : `Estimated time remaining: ${formatSecondsToMinSec(progress.estimatedTimeRemainingSeconds)}`}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Current activity */}
      {isRunning && progress.currentPage && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Currently crawling</div>
          <div className="mt-1.5 break-all rounded-lg bg-[var(--bg-tertiary)] px-3 py-2 font-mono text-xs text-[color:var(--text-secondary)] sm:text-sm">
            {progress.currentPage}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatTile
          icon={LuFileText}
          label="Pages Discovered"
          value={progress.pagesDiscovered}
          tone={{ bg: 'bg-[var(--primary-muted)]', text: 'text-blue-600' }}
        />
        <StatTile
          icon={LuLayers}
          label="Elements Mapped"
          value={progress.elementsMapped}
          tone={{ bg: 'bg-[var(--success-muted)]', text: 'text-emerald-600' }}
        />
        <StatTile
          icon={LuClipboardList}
          label="Forms Mapped"
          value={progress.formsMapped}
          tone={{ bg: 'bg-[rgba(139,92,246,0.12)]', text: 'text-violet-600' }}
        />
        <StatTile
          icon={LuRadar}
          label="Progress"
          value={`${isCompleted ? 100 : progress.progress}%`}
          tone={{ bg: 'bg-[var(--warning-muted)]', text: 'text-amber-600' }}
        />
      </div>

      {/* What's happening */}
      {isRunning && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">What's happening</h3>
          <ul className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {ACTIVITY_ITEMS.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-[color:var(--text-secondary)]">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--primary-color)]" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-4 border-t border-[var(--border-subtle)] pt-3 text-xs text-[color:var(--text-muted)]">
            You can leave this page — the crawl keeps running in the background.
          </p>
        </div>
      )}

      {/* Retry on failure */}
      {isFailed && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 text-center shadow-sm">
          <p className="text-sm text-[color:var(--text-secondary)]">Check the base URL and credentials, then try again.</p>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110"
          >
            <LuRotateCcw size={16} /> Retry Discovery
          </button>
        </div>
      )}
    </div>
  );
};

export default CrawlProgressPanel;
